import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { getProjectFilteringOptions } from "actions/RFQ/projectsAction";
import type { ProjectOption, ProjectTypeOption } from "../types/Notifications.type";

export interface ProjectFilterOptions {
	projectTypes: ProjectTypeOption[];
	projects: ProjectOption[];
}

// Reuses the existing project-filter-attribute endpoint (actions/RFQ/projectsAction.js)
// rather than a dedicated notifications-scope endpoint — none is defined in the frozen
// 106 contract for scope pickers. Response shape is defensively normalised since this
// endpoint isn't typed at its source.
const normalise = (raw: unknown): ProjectFilterOptions => {
	const data = (raw ?? {}) as Record<string, unknown>;
	const projectTypesRaw = (data.project_types ?? data.projectTypes ?? []) as unknown[];
	const projectsRaw = (data.projects ?? []) as unknown[];

	const toOption = (item: unknown): { id: number; name: string } | null => {
		if (!item || typeof item !== "object") {
			return null;
		}
		const record = item as Record<string, unknown>;
		const id = record.id ?? record.value;
		const name = record.name ?? record.label;
		if (typeof id !== "number" || typeof name !== "string") {
			return null;
		}
		return { id, name };
	};

	return {
		projectTypes: projectTypesRaw.map(toOption).filter((o): o is ProjectTypeOption => o !== null),
		projects: projectsRaw.map(toOption).filter((o): o is ProjectOption => o !== null),
	};
};

export const useProjectFilterOptions = (enabled: boolean): UseQueryResult<ProjectFilterOptions> => {
	return useQuery({
		queryKey: ["notifications", "project-filter-options"],
		queryFn: async () => normalise(await getProjectFilteringOptions()),
		enabled,
	});
};
